// Die Dekodierung: aus Bildern der Kamera wird eine Zeichenkette.
//
// Zwei Wege, weil die Browser sich nicht einig sind:
//
//   1. BarcodeDetector — im Browser eingebaut (Chrome, Android-WebView).
//      Nutzt die Bildverarbeitung des Systems, kostet nichts an
//      Ladezeit und ist deutlich schneller als alles in JavaScript.
//
//   2. @zxing/browser — nachgeladen, wenn es (1) nicht gibt. Das ist auf
//      iOS der REGELFALL, nicht der Ausnahmefall: Safari hat kein
//      BarcodeDetector, und iPad und iPhone sind die Hauptgeräte. Der
//      Weg darf sich deshalb nicht wie ein Notbehelf anfühlen.
//
// Warum überhaupt eine Bibliothek (CLAUDE.md: keine Abhängigkeiten ohne
// Not): Der Auftrag verlangt QR UND EAN-13. Für QR allein gäbe es
// kleinere Pakete (qr-scanner), aber die lesen keine Strichcodes — und
// der Strichcode vom Buchrücken ist der halbe Anwendungsfall
// (Auspacktisch). @zxing/browser steht unter MIT, ist reines JavaScript
// (keine WASM-Datei, die auf lima-city mit ausgeliefert und von der CSP
// erlaubt werden müsste) und wird per dynamischem `import` erst geladen,
// wenn jemand wirklich scannt — das Haupt-Bündel wächst dadurch nicht.
//
// Beide Wege haben dieselbe Form: starten, bei Treffer melden, aufhören.
// Was der Aufrufer zurückbekommt, ist die Stopp-Funktion.

export type DecoderStopp = () => void;

/** Der eingebaute Decoder, so viel wie wir davon brauchen. */
type NativerDetector = {
  detect: (quelle: CanvasImageSource) => Promise<{ rawValue?: string }[]>;
};
type NativerDetectorBau = {
  new (optionen?: { formats?: string[] }): NativerDetector;
  getSupportedFormats?: () => Promise<string[]>;
};

const GEBRAUCHT = ['qr_code', 'ean_13'];

/**
 * Code 128 — die Etiketten der Barsortimente (Befund 29.10.2026).
 *
 * Auf den Lieferetiketten von Umbreit/Libri steht die ISBN NICHT als
 * EAN-13, sondern als Code 128 („9 783…" darunter sieht nur so aus).
 * Nachgemessen am Foto des Betreibers mit zxing-cpp: dreimal
 * `Code 128 978…`. Der Scanner las davon nichts, weil er nur QR und
 * EAN-13 suchte.
 *
 * Erwünscht, aber nicht Bedingung: Ein eingebauter Decoder ohne
 * Code 128 taugt weiter für Zettel und Buchrücken.
 */
const ERWUENSCHT = ['code_128'];

/** Millisekunden zwischen zwei Leseversuchen. */
const TAKT = 200;

function nativerBau(): NativerDetectorBau | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { BarcodeDetector?: NativerDetectorBau }).BarcodeDetector;
}

/**
 * Kann der eingebaute Decoder beide Formate?
 *
 * Die Frage ist nicht überflüssig: BarcodeDetector gibt es auch in
 * Ausführungen, die nur QR können. Wer dann darauf setzt, hat einen
 * Scanner, der den Zettel liest und den Buchrücken stumm anschaut — und
 * niemand sähe, woran es liegt.
 */
async function nativeFormate(bau: NativerDetectorBau): Promise<string[] | null> {
  if (typeof bau.getSupportedFormats !== 'function') return null;
  try {
    const koennen = await bau.getSupportedFormats();
    if (!GEBRAUCHT.every((f) => koennen.includes(f))) return null;
    return [...GEBRAUCHT, ...ERWUENSCHT.filter((f) => koennen.includes(f))];
  } catch {
    return null;
  }
}

function nativStarten(
  bau: NativerDetectorBau,
  formate: string[],
  video: HTMLVideoElement,
  treffer: (roh: string) => void
): DecoderStopp {
  const detector = new bau({ formats: formate });
  let laeuft = true;

  async function schleife(): Promise<void> {
    while (laeuft) {
      try {
        const codes = await detector.detect(video);
        const wert = codes.find((c) => (c.rawValue ?? '') !== '')?.rawValue;
        if (wert !== undefined && laeuft) {
          laeuft = false;
          treffer(wert);
          return;
        }
      } catch {
        // Ein einzelnes Bild darf misslingen — etwa während der
        // Autofokus zieht. Das ist kein Grund, den Scanner zu beenden.
      }
      await new Promise((r) => setTimeout(r, TAKT));
    }
  }

  void schleife();
  return () => {
    laeuft = false;
  };
}

async function zxingStarten(
  video: HTMLVideoElement,
  treffer: (roh: string) => void
): Promise<DecoderStopp> {
  const { BrowserMultiFormatReader, BarcodeFormat } = await import('@zxing/browser');
  const leser = new BrowserMultiFormatReader(undefined, {
    delayBetweenScanAttempts: TAKT
  });
  // Nur die Formate, die wir wirklich brauchen. Ohne Einschränkung
  // probiert der Leser jedes Format durch — das kostet auf dem Tablet
  // spürbar Zeit und erfindet aus Bildrauschen gelegentlich einen
  // Code-39-Treffer. Code 128 seit 29.10.2026: Barsortiments-Etiketten.
  leser.possibleFormats = [BarcodeFormat.QR_CODE, BarcodeFormat.EAN_13, BarcodeFormat.CODE_128];

  let gemeldet = false;
  const steuerung = await leser.decodeFromVideoElement(video, (ergebnis) => {
    if (gemeldet || ergebnis === undefined) return;
    const wert = ergebnis.getText();
    if (wert === '') return;
    gemeldet = true;
    treffer(wert);
  });
  return () => steuerung.stop();
}

/**
 * Startet die Dekodierung auf einem laufenden Video-Element.
 *
 * Der Aufrufer bringt den Kamerastrom mit und ist auch für dessen Ende
 * zuständig — diese Datei fasst `getUserMedia` bewusst nicht an. Sonst
 * gäbe es zwei Stellen, die Spuren anhalten, und genau dort bleibt sonst
 * die Kameraleuchte an.
 */
export async function decoderStarten(
  video: HTMLVideoElement,
  treffer: (roh: string) => void
): Promise<DecoderStopp> {
  const bau = nativerBau();
  if (bau !== undefined) {
    const formate = await nativeFormate(bau);
    if (formate !== null) return nativStarten(bau, formate, video, treffer);
  }
  return zxingStarten(video, treffer);
}
